import React, { useState, useEffect } from "react";
import payrollService from "../../services/payrollService";
import Notification from "../../components/UI/Notification";
import { Copy, Check } from "lucide-react";

const ComponentTypeOptions = () => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);
  const [filterCategory, setFilterCategory] = useState("");

  useEffect(() => {
    fetchOptions();
  }, [filterCategory]);

  const fetchOptions = async () => {
    setLoading(true);
    try {
      const response = await payrollService.getComponentTypeOptions({
        category: filterCategory,
      });
      if (response.success) {
        setOptions(response.data);
      }
    } catch (error) {
      setNotification({ type: "error", message: "Failed to fetch component types" });
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
    setNotification({ type: "success", message: "Code copied to clipboard" });
  };

  return (
    <div className="p-6 font-poppins">
      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}

      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Component Type Options
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Browse and copy component type codes
        </p>
      </div>

      {/* Filter */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 mb-6">
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="w-full md:w-64 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
        >
          <option value="">All Categories</option>
          <option value="payroll">Payroll</option>
          <option value="benefits">Benefits</option>
          <option value="deductions">Deductions</option>
        </select>
      </div>

      {/* Options Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <div className="col-span-full flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : options.length > 0 ? (
          options.map((option) => (
            <div
              key={option.id}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 hover:shadow-lg transition-shadow"
            >
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    {option.label}
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    {option.category}
                  </p>
                </div>
                <button
                  onClick={() => handleCopyCode(option.value)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                  title="Copy code"
                >
                  {copiedCode === option.value ? (
                    <Check size={18} className="text-green-600" />
                  ) : (
                    <Copy size={18} className="text-gray-600 dark:text-gray-400" />
                  )}
                </button>
              </div>
              <div className="mt-3 p-2 bg-gray-100 dark:bg-gray-700 rounded-md">
                <code className="text-sm font-mono text-gray-900 dark:text-white">
                  {option.value}
                </code>
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-full text-center py-12 text-gray-500 dark:text-gray-400">
            No component types found
          </div>
        )}
      </div>
    </div>
  );
};

export default ComponentTypeOptions;

